import type { DtcCodeEntity } from '../../../dtc-codes/entities';

/**
 * Filters a list of search results to exclude DTC codes that already exist in the given list.
 * Useful when presenting search results to a user who has already added some codes to a case.
 *
 * @param results - The full list of search results to filter.
 * @param existingCodes - The codes to exclude from the results.
 * @returns A new array containing only codes not found in `existingCodes`.
 * @example
 * ```ts
 * const searchResults = await $searchDtcCodes({ query: 'P03' });
 * const newResults = filterOutExistingDtcCodes(searchResults, caseData.dtcCodes);
 * ```
 */
export function filterOutExistingDtcCodes(results: DtcCodeEntity[], existingCodes: DtcCodeEntity[]): DtcCodeEntity[] {
    if (!existingCodes?.length) {
        return results;
    }
    return results.filter((result) => !isExistingDtcCode(result, existingCodes));
}

/**
 * Checks whether a DTC code result already exists in a list of existing codes. Matches by `id` first
 * (exact identity), then falls back to matching by `code` value (logical equivalence).
 *
 * @param result - The search result to check.
 * @param existingCodes - The list of existing codes to compare against.
 * @returns True if the result matches any existing code by `id` or `code` value.
 * @example
 * ```ts
 * const isDuplicate = isExistingDtcCode(selectedCode, caseData.dtcCodes);
 * ```
 */
export function isExistingDtcCode(result: DtcCodeEntity, existingCodes: DtcCodeEntity[]): boolean {
    if (!result.code) {
        return false;
    }

    return existingCodes.some((existing) => {
        if (typeof result.id === 'string' && result.id === existing.id) {
            return true;
        }
        return result.code === existing.code;
    });
}
